/**
 * Pickup-line active windows.
 *
 * A line that defines both a dismissal time and an active duration is only
 * geofence-live around its own dismissal. Two lines may legitimately cover the
 * same ground when their windows do not overlap — that is how a half-day group
 * shares the main pickup spot — so this is what the admin UI checks before
 * letting two lines run at once over the same place.
 */

// Matches LINE_WINDOW_LEAD_MINUTES in the backend geofence service.
export const WINDOW_LEAD_MINUTES = 10;

export function parseTimeToMinutes(value) {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

export function formatMinutes(totalMinutes) {
  const normalized = ((Math.round(totalMinutes) % 1440) + 1440) % 1440;
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  const suffix = hours < 12 ? 'AM' : 'PM';
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return `${display}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

/**
 * The window a line is live for, or null when it has none (always live).
 * Returned as absolute minutes so a window crossing midnight stays comparable.
 */
export function getLineWindow(line) {
  const duration = Number(line?.active_duration_minutes);
  const start = parseTimeToMinutes(line?.dismissal_time);
  if (!Number.isInteger(duration) || duration <= 0 || start === null) return null;

  return { start: start - WINDOW_LEAD_MINUTES, end: start + duration };
}

function overlaps(a, b) {
  // Compare on a 2-day span so a window wrapping past midnight still lines up.
  const spans = (w) => (w.start < 0
    ? [{ start: w.start + 1440, end: w.end + 1440 }, { start: w.start, end: w.end }]
    : [{ start: w.start, end: w.end }, { start: w.start + 1440, end: w.end + 1440 }]);

  return spans(a).some((x) => spans(b).some((y) => x.start <= y.end && y.start <= x.end));
}

/**
 * Lines that are live at the same time as `line`.
 *
 * A line with no window is always live, so it conflicts with everything —
 * which is exactly the situation that caused one arrival to fire two enters.
 * Only geofenced, active lines can conflict; a line with no zone cannot.
 */
export function findScheduleConflicts(line, otherLines = []) {
  const isLive = (candidate) => candidate?.is_active && candidate?.geofenced_enabled;
  if (!isLive(line)) return [];

  const window = getLineWindow(line);

  return otherLines
    .filter((other) => Number(other?.carpool_line_id) !== Number(line?.carpool_line_id))
    .filter(isLive)
    .filter((other) => {
      const otherWindow = getLineWindow(other);
      if (!window || !otherWindow) return true; // one of them is always live
      return overlaps(window, otherWindow);
    });
}

export function describeWindow(line) {
  const window = getLineWindow(line);
  if (!window) return 'Live all day';
  return `Live ${formatMinutes(window.start)} – ${formatMinutes(window.end)}`;
}
